import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaAndroid,
  FaArrowRight,
  FaBolt,
  FaCheckCircle,
  FaExternalLinkAlt,
  FaQuestionCircle,
  FaShieldAlt,
  FaUsers,
  FaLayerGroup,
  FaChevronDown,
} from "react-icons/fa";

/* ─── animation helpers ─────────────────────────────────── */
const float1 = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  50%       { transform: translateY(-28px) scale(1.04); }
`;
const float2 = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  50%       { transform: translateY(22px) scale(0.97); }
`;
const float3 = keyframes`
  0%, 100% { transform: translateY(0) translateX(0); }
  33%       { transform: translateY(-18px) translateX(12px); }
  66%       { transform: translateY(10px) translateX(-8px); }
`;
const shimmer = keyframes`
  0%   { transform: translateX(-200%) skewX(-18deg); }
  100% { transform: translateX(400%)  skewX(-18deg); }
`;
const shimmerFast = keyframes`
  0%   { transform: translateX(-200%) skewX(-15deg); }
  100% { transform: translateX(400%)  skewX(-15deg); }
`;
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.55; }
`;
const pulseDot = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(41,171,226,0.5); }
  50%       { transform: scale(1.15); box-shadow: 0 0 0 6px rgba(41,171,226,0); }
`;
const scanMove = keyframes`
  0%   { transform: translateY(-100%); opacity: 0; }
  8%   { opacity: 1; }
  92%  { opacity: 1; }
  100% { transform: translateY(100vh); opacity: 0; }
`;
const radarRing = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.2); opacity: 0.8; }
  100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0; }
`;
const borderGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(41,171,226,0); border-color: rgba(41,171,226,0.2); }
  50%       { box-shadow: 0 0 18px 2px rgba(41,171,226,0.18); border-color: rgba(41,171,226,0.55); }
`;
const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
const typewriterBlink = keyframes`
  0%, 100% { border-right-color: rgba(41,171,226,0.8); }
  50%       { border-right-color: transparent; }
`;
const cardFloat = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-6px); }
`;
const iconSpin = keyframes`
  0%   { transform: rotate(0deg) scale(1); }
  25%  { transform: rotate(8deg) scale(1.08); }
  75%  { transform: rotate(-5deg) scale(0.96); }
  100% { transform: rotate(0deg) scale(1); }
`;

/* ─── framer variants ─────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};
const fadeIn = {
  hidden: { opacity: 0 },
  visible: (i = 0) => ({
    opacity: 1,
    transition: { duration: 0.45, delay: i * 0.06 },
  }),
};
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};
const chipAnim = {
  hidden: { opacity: 0, scale: 0.88 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: "backOut" } },
};

/* ─── product data ────────────────────────────────────────── */
const productSections = [
  {
    key: "signals",
    icon: FaAndroid,
    accent: "#29ABE2",
    accentDark: "#1a7fad",
    statusLabel: "Live",
    title: "Trend Genie AI Crypto Signals App",
    shortTitle: "Android Signals App",
    keyword: "AI crypto signals app",
    description:
      "The smartest mobile AI crypto trading companion. Get real-time AI-powered signals with precise entry, take-profit (TP1/TP2), and stop-loss levels for Bitcoin, Ethereum, and 1000+ altcoins.",
    features: [
      "Real-time AI crypto signals across hundreds of pairs",
      "Signal cards: entry · TP1 · TP2 · stop loss",
      "AI chart screenshot analysis",
      "Built-in AI coin research",
      "Live crypto news inside the app",
      "In-app trader community chat",
    ],
    pricing: "7-Day Free Trial · Then $10/mo",
    whoItsFor: "Traders who want accurate, structured AI signals without the guesswork.",
    ctaLabel: "Start Free Trial on Google Play",
    ctaHref: "https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie",
    proof: "10,000+ traders · 83%+ win rate on tracked signals",
    path: "/ai-crypto-signals-app",
  },
  {
    key: "rugshield",
    icon: FaShieldAlt,
    accent: "#10b981",
    accentDark: "#0a7a59",
    statusLabel: "Live",
    title: "RugShield DeFi Token Scanner",
    shortTitle: "RugShield DeFi Scanner",
    keyword: "DeFi safety scanner",
    description:
      "Protect yourself before you invest. RugShield detects honeypots, rug-pull risks, and smart contract vulnerabilities on Ethereum and BNB Chain — right in your browser.",
    features: [
      "Instant honeypot detection",
      "Rug-pull risk scoring",
      "Ownership & permission flag checks",
      "Liquidity lock status verification",
      "ERC-20 and BEP-20 support",
      "No wallet connection required",
    ],
    pricing: "Completely Free",
    whoItsFor: "DeFi traders who want contract-level protection before buying any token.",
    ctaLabel: "Scan Any Token Free Now",
    ctaHref: "https://app.trendgenie.io",
    proof: "Browser-based · No install · Results in seconds",
    path: "/rugshield-defi-scanner",
  },
  {
    key: "social",
    icon: FaUsers,
    accent: "#8B5CF6",
    accentDark: "#6d35d4",
    statusLabel: "Live",
    title: "Trend Genie Social",
    shortTitle: "Community Platform",
    keyword: "Web3 community + $TGEN",
    description:
      "Join a vibrant crypto-focused community where you discuss signals, share insights, and earn real utility through the $TGEN token — staking, quests, and airdrops included.",
    features: [
      "Crypto community feed & profiles",
      "Gamified daily rewards and quests",
      "Stake $TGEN for fixed APR",
      "Referral-driven $TGEN earning",
      "Airdrop program for active users",
      "Connected to signals & ecosystem",
    ],
    pricing: "$TGEN on DEX · CEX listings coming",
    whoItsFor: "Crypto users who want community, rewards, and ecosystem utility in one place.",
    ctaLabel: "Join Social & Start Earning $TGEN",
    ctaHref: "https://social.trendgenie.io",
    proof: "Staking · Quests · Airdrops · Referrals",
    path: "/products/social",
  },
  {
    key: "nova",
    icon: FaBolt,
    accent: "#F7941D",
    accentDark: "#c4700d",
    statusLabel: "Testing",
    title: "Trend Genie Nova",
    shortTitle: "Advanced AI Platform",
    keyword: "Advanced AI intelligence",
    description:
      "The next evolution of the Trend Genie ecosystem — a deeper AI intelligence layer with advanced market analysis, professional research workflows, and premium trading capabilities.",
    features: [
      "Deep AI market intelligence",
      "Multi-model AI research routing",
      "Structured outputs: summaries, plans",
      "Export to PDF, Markdown, CSV",
      "Advanced crypto workflow automation",
      "Premium tier for serious traders",
    ],
    pricing: "Coming Soon · Free tier planned",
    whoItsFor: "Advanced users ready for the next level beyond standard crypto signals.",
    ctaLabel: "Explore Nova Platform",
    ctaHref: "https://nova.trendgenie.io",
    proof: "The premium intelligence layer of the ecosystem",
    path: "/products/nova",
  },
];

const advantages = [
  { icon: FaLayerGroup, label: "Complete stack: signals + DeFi safety + community + research" },
  { icon: FaCheckCircle, label: "AI signals app — affordable $10/month after 7-day free trial" },
  { icon: FaShieldAlt, label: "Free RugShield scanner to stay safe in DeFi every time" },
  { icon: FaBolt, label: "Earn real $TGEN tokens through participation, staking, and referrals" },
  { icon: FaAndroid, label: "$TGEN live on DEX with major centralized exchange listings coming" },
  { icon: FaCheckCircle, label: "Transparent, high-performance tools backed by live results" },
];

const faqItems = [
  {
    question: "How does the Android app pricing work?",
    answer:
      "Enjoy a full 7-day free trial with complete access to all features. After that it is only $10 per month. Cancel anytime with no commitment.",
  },
  {
    question: "Is RugShield really free?",
    answer:
      "Yes. The RugShield DeFi safety scanner at app.trendgenie.io is completely free. No account or wallet connection required for a basic scan.",
  },
  {
    question: "What is $TGEN used for?",
    answer:
      "$TGEN powers rewards, staking, quests, and airdrops inside Trend Genie Social. It is currently trading on DEXs on BNB Chain with major centralized exchange listings planned soon.",
  },
  {
    question: "Do I need all four tools?",
    answer:
      "No. Start with the AI Signals App and RugShield for trading and safety. Then add Social for community rewards and Nova for deeper research as your workflow grows.",
  },
  {
    question: "Is this financial advice?",
    answer:
      "No. All tools provide information and decision support only. Always do your own research and never invest more than you can afford to lose.",
  },
];

/* ─── styled components ───────────────────────────────────── */

const Page = styled.div`
  background: var(--bg-main);
  color: var(--text-color);
  overflow-x: hidden;
`;

/* HERO */
const HeroBanner = styled.section`
  background: linear-gradient(135deg, #040c16 0%, #071525 40%, #08182a 70%, #060e1a 100%);
  padding: 5.5rem 8% 5rem;
  position: relative;
  overflow: hidden;
  min-height: 620px;
  display: flex;
  align-items: center;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(41,171,226,0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(41,171,226,0.06) 1px, transparent 1px);
    background-size: 48px 48px;
    mask-image: radial-gradient(ellipse 100% 100% at 30% 50%, black 10%, transparent 70%);
    pointer-events: none;
  }
`;

const HeroLayout = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 480px;
  gap: 4rem;
  align-items: center;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ScanLine = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  height: 1.5px;
  background: linear-gradient(90deg, transparent 0%, rgba(41,171,226,0.5) 25%, rgba(247,148,29,0.35) 55%, rgba(139,92,246,0.3) 80%, transparent 100%);
  animation: ${scanMove} 5s linear infinite;
  pointer-events: none;
`;

const ScanLine2 = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, rgba(139,92,246,0.4) 30%, rgba(41,171,226,0.3) 60%, transparent 100%);
  animation: ${scanMove} 7s linear infinite 2.5s;
  pointer-events: none;
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
`;

const Orb1 = styled(Orb)`
  width: 500px; height: 500px;
  background: radial-gradient(circle, rgba(41,171,226,0.22) 0%, transparent 70%);
  top: -160px; right: -60px;
  animation: ${float1} 11s ease-in-out infinite;
`;
const Orb2 = styled(Orb)`
  width: 350px; height: 350px;
  background: radial-gradient(circle, rgba(247,148,29,0.18) 0%, transparent 70%);
  bottom: -100px; left: 0%;
  animation: ${float2} 14s ease-in-out infinite;
`;
const Orb3 = styled(Orb)`
  width: 260px; height: 260px;
  background: radial-gradient(circle, rgba(139,92,246,0.2) 0%, transparent 70%);
  top: 20%; left: 35%;
  animation: ${float3} 9s ease-in-out infinite 1.5s;
`;
const Orb4 = styled(Orb)`
  width: 180px; height: 180px;
  background: radial-gradient(circle, rgba(16,185,129,0.15) 0%, transparent 70%);
  bottom: 10%; right: 30%;
  animation: ${float2} 12s ease-in-out infinite 3s;
`;

/* Radar rings */
const RadarWrap = styled.div`
  position: absolute;
  right: 6%;
  top: 50%;
  transform: translateY(-50%);
  width: 520px;
  height: 520px;
  pointer-events: none;
  z-index: 1;

  @media (max-width: 1100px) {
    display: none;
  }
`;

const RadarRing = styled.div`
  position: absolute;
  top: 50%; left: 50%;
  border-radius: 50%;
  border: 1px solid rgba(41,171,226,0.12);
  animation: ${radarRing} ${({ $dur }) => $dur || "4s"} ease-out infinite;
  animation-delay: ${({ $delay }) => $delay || "0s"};
  width: 200px; height: 200px;
`;

const HeroLeft = styled(motion.div)`
  position: relative;
  z-index: 2;
`;

const KickerBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.9rem 0.35rem 0.55rem;
  border-radius: 100px;
  background: rgba(41,171,226,0.1);
  border: 1px solid rgba(41,171,226,0.25);
  margin-bottom: 1.4rem;
  width: fit-content;
`;

const KickerDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #29ABE2;
  animation: ${pulseDot} 1.8s ease-in-out infinite;
  flex-shrink: 0;
`;

const KickerText = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #29ABE2;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.1rem, 4.5vw, 3.8rem);
  line-height: 1.07;
  font-weight: 900;
  letter-spacing: -0.03em;
  margin: 0 0 1.4rem;
  background: linear-gradient(135deg, #ffffff 0%, #cce9f8 38%, #29ABE2 65%, #F7941D 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent !important;
  background-clip: text;
  animation: ${gradientShift} 6s ease infinite;
`;

const HeroSub = styled(motion.p)`
  font-size: 1.05rem;
  line-height: 1.85;
  color: rgba(210,230,245,0.75) !important;
  -webkit-text-fill-color: rgba(210,230,245,0.75) !important;
  max-width: 600px;
  margin: 0 0 2.2rem;
`;

const HeroStats = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 1.6rem 2.6rem;
  margin-bottom: 2.4rem;
  padding: 1.25rem 1.5rem;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(41,171,226,0.12);
  border-radius: 18px;
  width: fit-content;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const StatNum = styled.span`
  font-size: 1.75rem;
  font-weight: 900;
  color: #ffffff;
  line-height: 1;
  background: linear-gradient(135deg, #fff 0%, #29ABE2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StatLabel = styled.span`
  font-size: 0.7rem;
  color: rgba(180,210,230,0.6);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
`;

const HeroCtas = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: center;
`;

const CtaPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.85rem 1.7rem;
  border-radius: 100px;
  background: linear-gradient(135deg, #29ABE2 0%, #1a7fad 100%);
  background-size: 200% 200%;
  color: #fff !important;
  -webkit-text-fill-color: #fff !important;
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  box-shadow: 0 4px 20px rgba(41,171,226,0.3);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
    transform: translateX(-150%) skewX(-15deg);
    transition: none;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(41,171,226,0.45);
    &::after { animation: ${shimmerFast} 0.6s ease forwards; }
  }
`;

const CtaSecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.82rem 1.5rem;
  border-radius: 100px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.15);
  color: rgba(220,238,250,0.9) !important;
  -webkit-text-fill-color: rgba(220,238,250,0.9) !important;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.22s ease, border-color 0.22s ease, transform 0.22s ease;

  &:hover {
    background: rgba(41,171,226,0.1);
    border-color: rgba(41,171,226,0.35);
    transform: translateY(-2px);
  }
`;

/* ── Hero right: product card stack ── */
const HeroRight = styled(motion.div)`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-width: 600px;
    margin: 0 auto;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const HeroProductCard = styled(motion.div)`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  padding: 1.25rem 1.1rem 1.1rem;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(12px);
  cursor: pointer;
  transition: border-color 0.3s ease;
  animation: ${cardFloat} ${({ $floatDur }) => $floatDur || "5s"} ease-in-out infinite;
  animation-delay: ${({ $floatDelay }) => $floatDelay || "0s"};

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${({ $accent }) => $accent}, ${({ $accent2 }) => $accent2 || "#29ABE2"});
    border-radius: 20px 20px 0 0;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent);
    transform: translateX(-140%);
    animation: ${shimmer} ${({ $shimmerDur }) => $shimmerDur || "5s"} ease-in-out infinite;
    animation-delay: ${({ $shimmerDelay }) => $shimmerDelay || "0s"};
  }

  &:hover {
    border-color: ${({ $accent }) => $accent}55;
    background: rgba(255,255,255,0.07);
    transform: translateY(-4px) !important;
    animation: none;
  }
`;

const HpcIconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${({ $accent }) => $accent}18;
  border: 1px solid ${({ $accent }) => $accent}30;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.85rem;
  color: ${({ $accent }) => $accent};
  font-size: 1.1rem;
`;

const HpcStatus = styled.span`
  position: absolute;
  top: 0.7rem;
  right: 0.75rem;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.22rem 0.55rem;
  border-radius: 100px;
  background: ${({ $live }) => $live ? "rgba(16,185,129,0.15)" : "rgba(247,148,29,0.15)"};
  color: ${({ $live }) => $live ? "#10b981" : "#F7941D"};
  border: 1px solid ${({ $live }) => $live ? "rgba(16,185,129,0.3)" : "rgba(247,148,29,0.3)"};
`;

const HpcName = styled.div`
  font-size: 0.92rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 0.3rem;
  line-height: 1.25;
`;

const HpcMeta = styled.div`
  font-size: 0.72rem;
  font-weight: 500;
  color: rgba(180,210,230,0.55);
  line-height: 1.4;
`;

/* SECTION WRAPPER */
const Section = styled.section`
  padding: 5rem 10% 0;
`;

const SectionLabel = styled(motion.p)`
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--primary-color);
  margin: 0 0 0.75rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(1.75rem, 3vw, 2.55rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
  margin: 0 0 0.9rem;
  color: var(--text-heading-alt) !important;
  -webkit-text-fill-color: var(--text-heading-alt) !important;
`;

const SectionSub = styled(motion.p)`
  color: var(--text-body-soft);
  line-height: 1.82;
  font-size: 0.98rem;
  max-width: 780px;
  margin: 0 0 2.5rem;
`;

/* PRODUCT CARDS GRID */
const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.6rem;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled(motion.article)`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 2px 20px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.28s ease, border-color 0.28s ease, transform 0.22s ease;
  cursor: default;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 52px rgba(0,0,0,0.12), 0 4px 16px ${({ $accent }) => $accent}22;
    border-color: ${({ $accent }) => $accent}55;
  }
`;

const CardTopBar = styled.div`
  height: 4px;
  background: linear-gradient(90deg, ${({ $accent }) => $accent}, ${({ $accentDark }) => $accentDark});
  flex-shrink: 0;
`;

const CardBody = styled.div`
  padding: 1.9rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const CardIconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${({ $accent }) => $accent}16;
  border: 1.5px solid ${({ $accent }) => $accent}30;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $accent }) => $accent};
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const BadgeGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.45rem;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.72rem;
  border-radius: 999px;
  font-size: 0.69rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${({ $live }) => ($live ? "rgba(16,185,129,0.12)" : "rgba(247,148,29,0.12)")};
  color: ${({ $live }) => ($live ? "var(--success-color)" : "var(--accent-color)")};
  border: 1px solid ${({ $live }) => ($live ? "rgba(16,185,129,0.28)" : "rgba(247,148,29,0.28)")};

  ${({ $live }) => $live && `
    &::before {
      content: "";
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--success-color);
    }
  `}
`;

const KeywordPill = styled.span`
  font-size: 0.7rem;
  color: var(--text-muted);
  letter-spacing: 0.04em;
`;

const CardTitle = styled.h2`
  font-size: 1.45rem;
  font-weight: 800;
  margin: 0 0 0.65rem;
  color: var(--text-heading-alt) !important;
  -webkit-text-fill-color: var(--text-heading-alt) !important;
  line-height: 1.2;
`;

const CardDesc = styled.p`
  color: var(--text-body-soft);
  line-height: 1.78;
  font-size: 0.94rem;
  margin: 0 0 1.4rem;
`;

const FeatureChips = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex: 1;
`;

const Chip = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  padding: 0.32rem 0.72rem;
  border-radius: 999px;
  background: ${({ $accent }) => $accent}0f;
  border: 1px solid ${({ $accent }) => $accent}28;
  color: var(--text-body-muted);
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.5;

  svg {
    color: ${({ $accent }) => $accent};
    font-size: 0.65rem;
    flex-shrink: 0;
  }
`;

const CardDivider = styled.div`
  height: 1px;
  background: var(--border-color);
  margin: 0 0 1.25rem;
`;

const CardMeta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
  margin-bottom: 1.35rem;
`;

const MetaBox = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 0.9rem 1rem;
`;

const MetaLabel = styled.p`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-muted);
  margin: 0 0 0.3rem;
`;

const MetaValue = styled.p`
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--text-strong);
  margin: 0;
  line-height: 1.5;
`;

const MetaText = styled.p`
  font-size: 0.83rem;
  color: var(--text-body-soft);
  margin: 0;
  line-height: 1.55;
`;

const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: auto;
`;

const CardPrimaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.78rem 1.1rem;
  border-radius: 12px;
  background: ${({ $accent }) => $accent};
  color: white !important;
  -webkit-text-fill-color: white !important;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.88rem;
  position: relative;
  overflow: hidden;
  transition: background 0.22s, transform 0.18s, box-shadow 0.22s;

  &::after {
    content: "";
    position: absolute;
    top: 0; left: 0;
    width: 36px; height: 100%;
    background: rgba(255,255,255,0.32);
    filter: blur(7px);
    transform: translateX(-200%) skewX(-18deg);
    transition: transform 0s;
  }

  &:hover {
    background: ${({ $accentDark }) => $accentDark};
    transform: translateY(-1px);
    box-shadow: 0 6px 20px ${({ $accent }) => $accent}44;

    &::after {
      animation: ${shimmer} 0.5s ease-in-out forwards;
    }
  }
`;

const CardSecondaryBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.78rem 1.1rem;
  border-radius: 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-body-soft) !important;
  -webkit-text-fill-color: var(--text-body-soft) !important;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.88rem;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-strong);
  }
`;

const ProofLine = styled.p`
  margin: 0.85rem 0 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--hero-text-faint);
`;

/* ECOSYSTEM FLOW */
const FlowSection = styled.section`
  padding: 5rem 10% 0;
`;

const FlowTrack = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 28px;
    left: 10%;
    right: 10%;
    height: 2px;
    background: linear-gradient(90deg, #29ABE2, #10b981, #8B5CF6, #F7941D);
    opacity: 0.25;
    border-radius: 2px;
  }

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;

    &::before { display: none; }
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const FlowStep = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 1.2rem;
  position: relative;
`;

const FlowDot = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ $accent }) => $accent}1a;
  border: 2px solid ${({ $accent }) => $accent}55;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $accent }) => $accent};
  font-size: 1.3rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
  box-shadow: 0 0 0 6px ${({ $accent }) => $accent}0a;
  transition: box-shadow 0.3s;

  ${FlowStep}:hover & {
    box-shadow: 0 0 0 12px ${({ $accent }) => $accent}14;
  }
`;

const FlowNum = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ $accent }) => $accent};
  color: white;
  font-size: 0.65rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FlowTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-heading-alt);
  margin: 0 0 0.4rem;
`;

const FlowText = styled.p`
  font-size: 0.82rem;
  color: var(--text-body-soft);
  line-height: 1.65;
  margin: 0;
`;

/* ADVANTAGES */
const AdvGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.1rem;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const AdvCard = styled(motion.div)`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 1.4rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  transition: transform 0.22s, box-shadow 0.22s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 32px rgba(0,0,0,0.08);
  }

  svg {
    color: var(--primary-color);
    flex-shrink: 0;
    margin-top: 2px;
    font-size: 1rem;
  }

  span {
    color: #394d5c;
    font-size: 0.93rem;
    line-height: 1.68;
    font-weight: 500;
  }
`;

/* FAQ */
const FaqList = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const FaqCard = styled(motion.div)`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 1px 8px rgba(0,0,0,0.04);
  transition: border-color 0.22s;

  &:hover { border-color: var(--border-strong); }
`;

const FaqTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.2rem 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
`;

const FaqQuestion = styled.span`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--text-strong);

  svg {
    color: var(--primary-color);
    flex-shrink: 0;
    font-size: 0.9rem;
  }
`;

const FaqChevron = styled(motion.span)`
  color: var(--text-muted);
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const FaqAnswer = styled(motion.div)`
  overflow: hidden;
`;

const FaqAnswerInner = styled.div`
  padding: 0 1.5rem 1.2rem;
  color: var(--text-body-soft);
  line-height: 1.78;
  font-size: 0.93rem;
`;

/* FINAL CTA */
const CtaSection = styled.section`
  padding: 5rem 10% 6rem;
`;

const CtaBanner = styled(motion.div)`
  background: var(--hero-gradient-strong);
  border-radius: 30px;
  padding: 3.5rem;
  position: relative;
  overflow: hidden;
  text-align: center;

  @media (max-width: 700px) { padding: 2.5rem 1.5rem; }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(var(--hero-overlay-grid) 1px, transparent 1px),
      linear-gradient(90deg, var(--hero-overlay-grid) 1px, transparent 1px);
    background-size: 48px 48px;
    mask-image: radial-gradient(ellipse 80% 80% at center, black 10%, transparent 70%);
    pointer-events: none;
  }
`;

const CtaOrb1 = styled.div`
  position: absolute;
  width: 320px; height: 320px;
  border-radius: 50%;
  background: var(--primary-color);
  filter: blur(100px);
  opacity: 0.12;
  top: -80px; left: -60px;
  pointer-events: none;
`;

const CtaOrb2 = styled.div`
  position: absolute;
  width: 240px; height: 240px;
  border-radius: 50%;
  background: var(--accent-color);
  filter: blur(90px);
  opacity: 0.1;
  bottom: -60px; right: -40px;
  pointer-events: none;
`;

const CtaTitle = styled.h2`
  font-size: clamp(1.8rem, 3.5vw, 2.8rem);
  font-weight: 800;
  color: var(--hero-text) !important;
  -webkit-text-fill-color: var(--hero-text) !important;
  margin: 0 0 1rem;
  position: relative;
  z-index: 1;
`;

const CtaSub = styled.p`
  color: var(--hero-text) !important;
  -webkit-text-fill-color: var(--hero-text) !important;
  font-size: 1.02rem;
  line-height: 1.78;
  max-width: 620px;
  margin: 0 auto 2.5rem;
  position: relative;
  z-index: 1;
`;

const CtaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.85rem;
  position: relative;
  z-index: 1;
`;

const CtaBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.9rem 1.4rem;
  border-radius: 13px;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
  background: ${({ $accent }) => $accent || "var(--primary-color)"};
  color: white !important;
  -webkit-text-fill-color: white !important;
  transition: background 0.22s, transform 0.18s, box-shadow 0.22s;

  &:hover {
    background: ${({ $accentDark }) => $accentDark || "var(--primary-dark)"};
    transform: translateY(-2px);
    box-shadow: 0 8px 24px ${({ $accent }) => $accent || "#29abe2"}44;
  }
`;

const TrustRow = styled.p`
  margin: 1.5rem 0 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--hero-text) !important;
  -webkit-text-fill-color: var(--hero-text) !important;
  position: relative;
  z-index: 1;
`;

/* ─── accordion hook ──────────────────────────────────────── */
function FaqItem({ item, index }) {
  const [open, setOpen] = useState(false);

  return (
    <FaqCard
      custom={index}
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
    >
      <FaqTrigger onClick={() => setOpen(!open)}>
        <FaqQuestion>
          <FaQuestionCircle />
          {item.question}
        </FaqQuestion>
        <FaqChevron
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25 }}
        >
          <FaChevronDown />
        </FaqChevron>
      </FaqTrigger>
      <AnimatePresence initial={false}>
        {open && (
          <FaqAnswer
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <FaqAnswerInner>{item.answer}</FaqAnswerInner>
          </FaqAnswer>
        )}
      </AnimatePresence>
    </FaqCard>
  );
}

/* ─── page component ──────────────────────────────────────── */
function ProductsPage() {
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Trend Genie AI Crypto Signals App for Android", url: "https://www.trendgenie.io/products/android-app" },
      { "@type": "ListItem", position: 2, name: "RugShield - AI-Powered DeFi Safety Scanner", url: "https://www.trendgenie.io/rugshield-defi-scanner" },
      { "@type": "ListItem", position: 3, name: "Trend Genie Social - Web3 Community and $TGEN Rewards Platform", url: "https://www.trendgenie.io/products/social" },
      { "@type": "ListItem", position: 4, name: "GenieNova - Real-Time AI Research Workspace by Trend Genie", url: "https://www.trendgenie.io/products/nova" },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };

  return (
    <Page>
      <Helmet>
        <title>Trend Genie Products | AI Crypto Signals App, DeFi Token Scanner &amp; Trading Tools</title>
        <meta name="description" content="Explore Trend Genie's 4 powerful tools: AI Crypto Signals App (7-day free trial then $10/mo), RugShield DeFi Safety Scanner (free), Trend Genie Social with $TGEN rewards, and Nova AI research workspace." />
        <meta name="keywords" content="Trend Genie products, AI crypto signals app, RugShield DeFi scanner, Trend Genie Social, $TGEN token, GenieNova AI, best AI crypto signals app 2026, free DeFi rug pull scanner, earn $TGEN rewards" />
        <meta property="og:title" content="Trend Genie Products | AI Crypto Signals App, RugShield, Social and Nova" />
        <meta property="og:description" content="Four powerful AI and Web3 tools to trade smarter, stay safe in DeFi, earn rewards, and research faster in 2026." />
        <meta property="og:url" content="https://www.trendgenie.io/products" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.trendgenie.io/products" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.trendgenie.io" },
            { "@type": "ListItem", position: 2, name: "Products", item: "https://www.trendgenie.io/products" },
          ],
        })}</script>
      </Helmet>

      {/* ── HERO ── */}
      <HeroBanner>
        <Orb1 /><Orb2 /><Orb3 /><Orb4 />
        <ScanLine />
        <ScanLine2 />
        <RadarWrap>
          <RadarRing $dur="4s" $delay="0s" />
          <RadarRing $dur="4s" $delay="1.1s" />
          <RadarRing $dur="4s" $delay="2.2s" />
          <RadarRing $dur="4s" $delay="3.3s" />
        </RadarWrap>

        <HeroLayout>
          {/* ── Left column ── */}
          <HeroLeft>
            <KickerBadge
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <KickerDot />
              <KickerText>AI Product Ecosystem · 4 Tools. One Platform.</KickerText>
            </KickerBadge>

            <HeroTitle
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              AI-Powered Crypto Tools for Every Trader
            </HeroTitle>

            <HeroSub
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
            >
              Real-time AI signals. DeFi safety scanner. Community rewards.
              Advanced research workspace. One connected ecosystem engineered
              for active crypto traders — from beginners to professionals.
            </HeroSub>

            <HeroStats
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.36 }}
            >
              {[
                { num: "4", label: "Live Products" },
                { num: "10K+", label: "Active Traders" },
                { num: "83%+", label: "Signal Win Rate" },
                { num: "$10/mo", label: "Signals App" },
              ].map((s) => (
                <StatItem key={s.label}>
                  <StatNum>{s.num}</StatNum>
                  <StatLabel>{s.label}</StatLabel>
                </StatItem>
              ))}
            </HeroStats>

            <HeroCtas
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.48 }}
            >
              <CtaPrimary
                href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaAndroid /> Start Free Trial
              </CtaPrimary>
              <CtaSecondary to="/rugshield-defi-scanner">
                <FaShieldAlt /> Try RugShield Free
              </CtaSecondary>
            </HeroCtas>
          </HeroLeft>

          {/* ── Right column: product card stack ── */}
          <HeroRight
            initial="hidden"
            animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1, delayChildren: 0.3 } } }}
          >
            {[
              {
                icon: FaAndroid, accent: "#29ABE2", accent2: "#1a7fad",
                name: "Trend Genie: AI Analysis", meta: "Real-time crypto signals with entry, TP & SL",
                status: "Live", floatDur: "6s", floatDelay: "0s", shimmerDur: "5s", shimmerDelay: "0s",
                path: "/ai-crypto-signals-app",
              },
              {
                icon: FaShieldAlt, accent: "#10b981", accent2: "#0a7a59",
                name: "RugShield Scanner", meta: "DeFi honeypot & rug-pull protection",
                status: "Live", floatDur: "7s", floatDelay: "0.8s", shimmerDur: "6s", shimmerDelay: "1s",
                path: "/rugshield-defi-scanner",
              },
              {
                icon: FaUsers, accent: "#8B5CF6", accent2: "#6d35d4",
                name: "Genie Social", meta: "Crypto community, quests & $TGEN rewards",
                status: "Live", floatDur: "5.5s", floatDelay: "1.5s", shimmerDur: "4.5s", shimmerDelay: "2s",
                path: "/products/social",
              },
              {
                icon: FaBolt, accent: "#F7941D", accent2: "#c4700d",
                name: "Genie Nova", meta: "Advanced AI research & market intelligence",
                status: "Testing", floatDur: "8s", floatDelay: "0.4s", shimmerDur: "7s", shimmerDelay: "0.5s",
                path: "/products/nova",
              },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <HeroProductCard
                  key={p.name}
                  as={Link}
                  to={p.path}
                  $accent={p.accent}
                  $accent2={p.accent2}
                  $floatDur={p.floatDur}
                  $floatDelay={p.floatDelay}
                  $shimmerDur={p.shimmerDur}
                  $shimmerDelay={p.shimmerDelay}
                  variants={{
                    hidden: { opacity: 0, y: 22, scale: 0.94 },
                    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
                  }}
                  style={{ textDecoration: "none" }}
                >
                  <HpcStatus $live={p.status === "Live"}>{p.status}</HpcStatus>
                  <HpcIconWrap $accent={p.accent}>
                    <Icon />
                  </HpcIconWrap>
                  <HpcName>{p.name}</HpcName>
                  <HpcMeta>{p.meta}</HpcMeta>
                </HeroProductCard>
              );
            })}
          </HeroRight>
        </HeroLayout>
      </HeroBanner>

      {/* ── PRODUCTS GRID ── */}
      <Section>
        <SectionLabel
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          All Products
        </SectionLabel>
        <SectionTitle
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Four World-Class Tools. One Powerful Ecosystem.
        </SectionTitle>
        <SectionSub
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          Trend Genie combines real-time AI trading intelligence, DeFi safety scanning, Web3
          community rewards, and professional AI research. Each tool solves a specific problem —
          together they create a stronger workflow for every type of crypto trader.
        </SectionSub>

        <ProductsGrid>
          {productSections.map((product, index) => {
            const Icon = product.icon;
            const isLive = product.statusLabel === "Live";

            return (
              <ProductCard
                key={product.key}
                $accent={product.accent}
                custom={index}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
              >
                <CardTopBar $accent={product.accent} $accentDark={product.accentDark} />
                <CardBody>
                  <CardHeader>
                    <CardIconWrap $accent={product.accent}>
                      <Icon />
                    </CardIconWrap>
                    <BadgeGroup>
                      <StatusBadge $live={isLive}>{product.statusLabel}</StatusBadge>
                      <KeywordPill>{product.keyword}</KeywordPill>
                    </BadgeGroup>
                  </CardHeader>

                  <CardTitle>{product.title}</CardTitle>
                  <CardDesc>{product.description}</CardDesc>

                  <FeatureChips
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                  >
                    {product.features.map((f) => (
                      <Chip key={f} $accent={product.accent} variants={chipAnim}>
                        <FaCheckCircle />
                        {f}
                      </Chip>
                    ))}
                  </FeatureChips>

                  <CardDivider />

                  <CardMeta>
                    <MetaBox>
                      <MetaLabel>Pricing</MetaLabel>
                      <MetaValue>{product.pricing}</MetaValue>
                    </MetaBox>
                    <MetaBox>
                      <MetaLabel>Best For</MetaLabel>
                      <MetaText>{product.whoItsFor}</MetaText>
                    </MetaBox>
                  </CardMeta>

                  <CardActions>
                    <CardPrimaryBtn
                      href={product.ctaHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      $accent={product.accent}
                      $accentDark={product.accentDark}
                    >
                      {product.ctaLabel} <FaExternalLinkAlt style={{ fontSize: "0.72rem" }} />
                    </CardPrimaryBtn>
                    <CardSecondaryBtn to={product.path}>
                      Details <FaArrowRight />
                    </CardSecondaryBtn>
                  </CardActions>

                  <ProofLine>{product.proof}</ProofLine>
                </CardBody>
              </ProductCard>
            );
          })}
        </ProductsGrid>
      </Section>

      {/* ── ECOSYSTEM FLOW ── */}
      <FlowSection>
        <SectionLabel
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          How It Works
        </SectionLabel>
        <SectionTitle
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          The Recommended Ecosystem Workflow
        </SectionTitle>
        <SectionSub
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          Start with AI signals, verify every token with RugShield, discuss and earn on Social,
          then go deeper with Nova. Each step builds on the last.
        </SectionSub>

        <FlowTrack>
          {[
            { num: 1, icon: FaAndroid, accent: "#29ABE2", title: "Get AI Signals", text: "Download the Android app and scan the market for real-time AI trade setups." },
            { num: 2, icon: FaShieldAlt, accent: "#10b981", title: "Verify Token Safety", text: "Before buying any DeFi token, run a RugShield scan to check for honeypots." },
            { num: 3, icon: FaUsers, accent: "#8B5CF6", title: "Discuss & Earn", text: "Share ideas on Social, earn $TGEN rewards, and join the growing community." },
            { num: 4, icon: FaBolt, accent: "#F7941D", title: "Go Deeper with Nova", text: "Use Nova for advanced AI research, structured analysis, and deeper market intel." },
          ].map((step, i) => {
            const StepIcon = step.icon;
            return (
              <FlowStep
                key={step.num}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
              >
                <FlowDot $accent={step.accent}>
                  <StepIcon />
                  <FlowNum $accent={step.accent}>{step.num}</FlowNum>
                </FlowDot>
                <FlowTitle>{step.title}</FlowTitle>
                <FlowText>{step.text}</FlowText>
              </FlowStep>
            );
          })}
        </FlowTrack>
      </FlowSection>

      {/* ── WHY CHOOSE ── */}
      <Section>
        <SectionLabel
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Why Trend Genie
        </SectionLabel>
        <SectionTitle
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Why Traders Choose Trend Genie in 2026
        </SectionTitle>

        <AdvGrid>
          {advantages.map((adv, i) => {
            const AdvIcon = adv.icon;
            return (
              <AdvCard
                key={adv.label}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
              >
                <AdvIcon />
                <span>{adv.label}</span>
              </AdvCard>
            );
          })}
        </AdvGrid>
      </Section>

      {/* ── FAQ ── */}
      <Section>
        <SectionLabel
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          FAQ
        </SectionLabel>
        <SectionTitle
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Frequently Asked Questions
        </SectionTitle>

        <FaqList>
          {faqItems.map((item, index) => (
            <FaqItem key={item.question} item={item} index={index} />
          ))}
        </FaqList>
      </Section>

      {/* ── FINAL CTA ── */}
      <CtaSection>
        <CtaBanner
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <CtaOrb1 />
          <CtaOrb2 />
          <CtaTitle>Ready to Trade Smarter and Safer?</CtaTitle>
          <CtaSub>
            Start with the 7-day free trial of the Trend Genie: AI Analysis, scan your first token with
            RugShield for free, and join the Social community to start earning $TGEN today.
          </CtaSub>

          <CtaGrid>
            <CtaBtn
              href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
              target="_blank"
              rel="noopener noreferrer"
              $accent="#29ABE2"
              $accentDark="#1a7fad"
            >
              <FaAndroid /> Download Trend Genie: AI Analysis
            </CtaBtn>
            <CtaBtn
              href="https://app.trendgenie.io"
              target="_blank"
              rel="noopener noreferrer"
              $accent="#10b981"
              $accentDark="#0a7a59"
            >
              <FaShieldAlt /> Open RugShield Free
            </CtaBtn>
            <CtaBtn
              href="https://social.trendgenie.io"
              target="_blank"
              rel="noopener noreferrer"
              $accent="#8B5CF6"
              $accentDark="#6d35d4"
            >
              <FaUsers /> Join Social &amp; Earn $TGEN
            </CtaBtn>
            <CtaBtn
              href="https://nova.trendgenie.io"
              target="_blank"
              rel="noopener noreferrer"
              $accent="#F7941D"
              $accentDark="#c4700d"
            >
              <FaBolt /> Explore Nova
            </CtaBtn>
          </CtaGrid>

          <TrustRow>
            10,000+ traders · Live performance tracking · $TGEN ecosystem
          </TrustRow>
        </CtaBanner>
      </CtaSection>
    </Page>
  );
}

export default ProductsPage;
